/*!
 * XES Cost-per-Bale Calculator (PDP)
 * Built 2026-05-09. Lives behind ?view=calc.
 *
 * Math:
 *   wrap_per_bale_ft = π × diameter_ft × wraps
 *   bales_per_roll   = roll_length_ft / wrap_per_bale_ft
 *   cost_per_bale    = roll_price / bales_per_roll
 *
 * Sources cited on UI:
 *   - Texas A&M Extension ANSC-PU-157 (Banta, 2022) — width × diameter convention
 *   - Verified XES customer review B.N. (Jul 2025) — quoted verbatim; XES recommendation = 3 wraps (2026-05-10)
 */
(function () {
  'use strict';

  // ---- Bale-size catalog (from TAMU Table 1) ----
  // Five sizes that matter on US round balers. width / diameter both in feet.
  // Width 4ft → XES 48"/51" rolls. Width 5ft → XES 64"/67" rolls.
  var SIZES = [
    { id: '4x4', width: 4, diameter: 4, label: '4 × 4 ft  (small bale, ~563 lb)',  chamber: 4 },
    { id: '4x5', width: 4, diameter: 5, label: '4 × 5 ft  (typical 4-ft, ~880 lb)', chamber: 4 },
    { id: '4x6', width: 4, diameter: 6, label: '4 × 6 ft  (large 4-ft, ~1,267 lb)', chamber: 4 },
    { id: '5x5', width: 5, diameter: 5, label: '5 × 5 ft  (~1,100 lb)',              chamber: 5 },
    { id: '5x6', width: 5, diameter: 6, label: '5 × 6 ft  (large round, ~1,584 lb)', chamber: 5 }
  ];
  var DEFAULT_BY_CHAMBER = { 4: '4x5', 5: '5x6' };

  // XES recommended wraps for our patented knit. 3 used in calc (Lili approved 2026-05-10).
  var XES_WRAPS = 3;

  // ---- Currency formatting ----
  function fmtMoney(n, sym) {
    if (!isFinite(n)) return (sym || '$') + '0.00';
    var sign = n < 0 ? '-' : '';
    var abs = Math.abs(n);
    return sign + (sym || '$') + abs.toFixed(2);
  }
  function fmtMoneyRounded(n, sym) {
    if (!isFinite(n)) return (sym || '$') + '0';
    var sign = n < 0 ? '-' : '';
    var abs = Math.abs(n);
    return sign + (sym || '$') + Math.round(abs).toLocaleString('en-US');
  }
  function fmtBales(n) {
    if (!isFinite(n) || n <= 0) return '— bales / roll';
    return Math.round(n).toLocaleString('en-US') + ' bales / roll';
  }

  // ---- Variant parsing ----
  // Variant title shape: "48 in. x 9,840 ft. / Grass Green"
  function parseVariant(line) {
    var parts = line.split('|');
    if (parts.length < 3) return null;
    var id = parts[0];
    var title = parts[1];
    var price = parseFloat(parts[2]);
    // Defensive: if the Liquid template ever emits cents (integer >= 1000) instead of dollars,
    // recover by dividing by 100. No real net-wrap roll costs $1,000+.
    if (isFinite(price) && price >= 1000 && Number.isInteger(price)) {
      price = price / 100;
    }
    if (!isFinite(price)) price = 0;
    var widthMatch  = title.match(/(\d{2,3})\s*in/i);
    var lengthMatch = title.match(/([\d,]{3,7})\s*ft/i);
    return {
      id: id,
      title: title,
      price: price,
      width: widthMatch  ? parseInt(widthMatch[1], 10) : null,
      length: lengthMatch ? parseInt(lengthMatch[1].replace(/,/g, ''), 10) : null
    };
  }

  function chamberFromWidth(w) {
    if (w === 48 || w === 51) return 4;
    if (w === 64 || w === 67) return 5;
    return null;
  }

  // ---- Calculation core ----
  function compute(diameter, wraps, lengthFt, price) {
    if (!(diameter > 0 && wraps > 0 && lengthFt > 0 && price > 0)) {
      return { wrapPerBale: 0, balesPerRoll: 0, costPerBale: 0 };
    }
    var wrapPerBale = Math.PI * diameter * wraps;
    var balesPerRoll = lengthFt / wrapPerBale;
    var costPerBale = price / balesPerRoll;
    return { wrapPerBale: wrapPerBale, balesPerRoll: balesPerRoll, costPerBale: costPerBale };
  }

  // ---- Wire one calculator instance ----
  function init(root) {
    if (!root || root.__cpbInited) return;
    root.__cpbInited = true;

    var currency = root.getAttribute('data-currency') || '$';
    var rawVariants = root.getAttribute('data-variants') || '';
    var variants = rawVariants.split('||').map(parseVariant).filter(Boolean);
    if (variants.length === 0) {
      console.warn('[cpb] no variants parsed from data-variants');
      return;
    }

    var initialId = root.getAttribute('data-current-id');
    var currentVariant =
      variants.find(function (v) { return String(v.id) === String(initialId); }) ||
      variants[0];

    var sel = {
      size:        root.querySelector('[data-cpb-size]'),
      wraps:       root.querySelector('[data-cpb-wraps]'),
      wrapsOut:    root.querySelector('[data-cpb-wraps-out]'),
      compPrice:   root.querySelector('[data-cpb-comp-price]'),
      compLength:  root.querySelector('[data-cpb-comp-length]'),
      bales:       root.querySelector('[data-cpb-bales]'),
      outComp:     root.querySelector('[data-cpb-out-comp]'),
      outCompBales:root.querySelector('[data-cpb-out-comp-bales]'),
      outXes:      root.querySelector('[data-cpb-out-xes]'),
      outXesBales: root.querySelector('[data-cpb-out-xes-bales]'),
      outSaveBale: root.querySelector('[data-cpb-out-save-bale]'),
      outSaveSeas: root.querySelector('[data-cpb-out-save-season]'),
      saveLabel:   root.querySelector('[data-cpb-savings-label]'),
      saveCard:    root.querySelector('.cpb__result--save')
    };

    function rebuildSizeOptions() {
      var chamber = chamberFromWidth(currentVariant.width) || 5;
      // Show ALL sizes regardless of chamber so users can compare any bale size
      // they actually run, not just the ones that match the SKU they're viewing.
      // Default still snaps to the chamber-appropriate size.
      var preferred = DEFAULT_BY_CHAMBER[chamber];
      var prevValue = sel.size.value;
      sel.size.innerHTML = '';
      SIZES.forEach(function (s) {
        var opt = document.createElement('option');
        opt.value = s.id;
        opt.textContent = s.label;
        sel.size.appendChild(opt);
      });
      // Keep prior selection if still valid; otherwise fall to chamber default.
      var canKeep = SIZES.some(function (s) { return s.id === prevValue; });
      sel.size.value = canKeep ? prevValue : preferred;
    }

    function getSize() {
      return SIZES.find(function (s) { return s.id === sel.size.value; })
          || SIZES.find(function (s) { return s.id === DEFAULT_BY_CHAMBER[chamberFromWidth(currentVariant.width) || 5]; })
          || SIZES[1];
    }

    function syncCompLengthDefault() {
      // Snap competitor roll length default to current XES variant length
      // ONLY if the user hasn't manually edited it.
      if (sel.compLength.dataset.touched === '1') return;
      if (currentVariant.length) sel.compLength.value = currentVariant.length;
    }

    function render() {
      var size = getSize();
      var wraps = parseFloat(sel.wraps.value);
      var compPrice  = parseFloat(sel.compPrice.value);
      var compLength = parseFloat(sel.compLength.value);
      var bales = parseFloat(sel.bales.value);

      sel.wrapsOut.textContent = wraps.toFixed(2).replace(/\.00$/, '').replace(/\.(\d)0$/, '.$1');

      // Update range track gradient
      var pct = ((wraps - 2) / (5 - 2)) * 100;
      sel.wraps.style.background =
        'linear-gradient(to right, var(--cpb-accent) 0%, var(--cpb-accent) ' + pct + '%, var(--cpb-border) ' + pct + '%)';

      // Their wrap (uses their inputs)
      var theirs = compute(size.diameter, wraps, compLength, compPrice);

      // XES (uses current variant's price + length, fixed at 3 wraps)
      var xes = compute(size.diameter, XES_WRAPS, currentVariant.length || compLength, currentVariant.price);

      sel.outComp.textContent = fmtMoney(theirs.costPerBale, currency) + ' / bale';
      sel.outCompBales.textContent = fmtBales(theirs.balesPerRoll);
      sel.outXes.textContent = fmtMoney(xes.costPerBale, currency) + ' / bale';
      sel.outXesBales.textContent = fmtBales(xes.balesPerRoll);

      // Savings (positive = XES wins)
      var savePerBale = theirs.costPerBale - xes.costPerBale;
      var saveSeason = savePerBale * (isFinite(bales) ? bales : 0);

      if (savePerBale >= 0) {
        sel.saveLabel.textContent = 'You save with XES';
        sel.outSaveBale.textContent = fmtMoney(savePerBale, currency) + ' / bale';
        sel.outSaveSeas.textContent = fmtMoneyRounded(saveSeason, currency) + ' / season';
        sel.saveCard.classList.remove('is-loss');
      } else {
        sel.saveLabel.textContent = 'Your wrap is cheaper at these inputs';
        sel.outSaveBale.textContent = fmtMoney(Math.abs(savePerBale), currency) + ' / bale';
        sel.outSaveSeas.textContent = fmtMoneyRounded(Math.abs(saveSeason), currency) + ' / season';
        sel.saveCard.classList.add('is-loss');
      }
    }

    // ---- React to variant changes on the PDP ----
    function onVariantChange(newId) {
      var match = variants.find(function (v) { return String(v.id) === String(newId); });
      if (!match) return;
      currentVariant = match;
      rebuildSizeOptions();
      syncCompLengthDefault();
      render();
    }

    // Hook the hidden [name="id"] input the variant_picker block keeps updated.
    function attachVariantHook() {
      // Re-find on every change because Shopify can re-render.
      document.addEventListener('change', function (e) {
        var t = e.target;
        if (!t) return;
        if (t.name === 'id' && t.tagName === 'INPUT') {
          onVariantChange(t.value);
          return;
        }
        // Also catch the radio buttons in fieldset[name^="variants"]
        if (t.tagName === 'INPUT' && t.closest && t.closest('variant-selects')) {
          // Defer so VariantSelects can finish updating [name="id"]
          setTimeout(function () {
            var hidden = document.querySelector('variant-selects [name="id"], input[name="id"]');
            if (hidden) onVariantChange(hidden.value);
          }, 0);
        }
      }, true);
    }

    // ---- User input wiring ----
    sel.size.addEventListener('change', render);
    sel.wraps.addEventListener('input', render);
    sel.compPrice.addEventListener('input', render);
    sel.compLength.addEventListener('input', function () {
      sel.compLength.dataset.touched = '1';
      render();
    });
    sel.bales.addEventListener('input', render);
    attachVariantHook();

    rebuildSizeOptions();
    syncCompLengthDefault();
    render();
  }

  function boot() {
    document.querySelectorAll('[data-cpb]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
